
import { FC } from 'react'
import * as tableStyles from 'styles/molecules/tables/Table2.css'
import { Transaction, TransactionsAppState, FullMoneyflowTyped, TransactionUid } from 'interfaces/moneyflow'
import { Table2StateProps, TableDispatchProps } from 'components/molecules/tables/Table'
import { SortingProp } from 'interfaces/table'
import { EmptyCardBlock } from 'components/features/pfm/Blocks/Blocks'
import Table2 from 'components/molecules/tables/Table2'
import { PaginatorStateProps } from 'components/molecules/tables/Paginator'
import { PageSizeSelectorStateProps } from 'components/atoms/tables/PageSizeSelector'
import TransactionRow from 'components/molecules/tables/TransactionRow'
import TransactionRowMobile from 'components/molecules/tables/TransactionRowMobile'
import { Collection, Omit } from 'interfaces'
import { getItemsFromCollection } from 'helpers/normalizeHelpers'
import CommonHeaderMobile from 'components/molecules/tables/CommonHeaderMobile'
import TransactionsHeader from 'components/molecules/tables/TransactionsHeader'
import Pagination from 'components/features/pfm/Pagination/Pagination'
import { NoAccountsCard } from 'components/features/pfm/NoAccountsCard/NoAccountsCard'

export type TransactionsStateProps =
  & Omit<Table2StateProps<Transaction>, 'rows'>
  & Pick<TransactionsAppState, 'editingId' | 'sorting'>
  & {
    pageSize: PageSizeSelectorStateProps
    paginator: PaginatorStateProps
    isMobile: boolean
    rowsCollection: Collection<Transaction, 'uid'>
    hasNoLinkedAccounts?: boolean
  }

export type TransactionsDispatchProps = TableDispatchProps & {
  onReadonlyCategoryClick: (transaction: Transaction) => void
  toggleTransactionEdit: (uid: TransactionUid, toggleValue: boolean) => void
}

export type TransactionsOwnProps = FullMoneyflowTyped & {
  defaultSorting?: SortingProp
}

type TransactionsModuleProps =
  & TransactionsStateProps
  & TransactionsDispatchProps
  & TransactionsOwnProps

const getRowKey = ({ uid }: Transaction) => String(uid)
const getDate = ({ date }: Transaction) => date

const TransactionsModule: FC<TransactionsModuleProps> = (props) => {
  const {
    isMobile,
    rowsCollection,
    editingId,
    type,

    changeSorting,
    toggleTransactionEdit,

    paginator,
    pageSize,
    onPageClick,
    onPageSizeClick,
    onReadonlyCategoryClick,
    hasNoLinkedAccounts,
  } = props

  const rows = getItemsFromCollection(rowsCollection)

  if (!rows || rows.length === 0) {
    if (hasNoLinkedAccounts) {
      return (
        <NoAccountsCard>
          See all your transactions in one place.
        </NoAccountsCard>
      )
    }

    return (
      <EmptyCardBlock>
        You have no transactions for selected category.
      </EmptyCardBlock>
    )
  }

  const RowComponent = isMobile ? TransactionRowMobile : TransactionRow
  const HeaderComponent = isMobile ? () => <CommonHeaderMobile type={ type } /> : TransactionsHeader

  const breakSpanPredicate = Boolean(editingId)
    ? ({ uid }: Transaction) => uid === editingId
    : undefined

  return (
    <>
      <div className={ tableStyles.transactions }>
        <Table2
          isLoading={ props.isLoading }
          sorting={ props.sorting }
          rows={ rows }
          getRowKey={ getRowKey }

          onChangeSorting={ changeSorting }
        >
          <Table2.HeadRowMarker>
            <HeaderComponent />
          </Table2.HeadRowMarker>
          <Table2.Rows>
            <Table2.RowSpan name='date' breakPredicate={ breakSpanPredicate }>
              { getDate }
            </Table2.RowSpan>
            <Table2.RowTemplate>
              {
                (row: Transaction) => (
                  <RowComponent
                    key={ row.uid }
                    type={ type }
                    row={ row }
                    isEditing={ editingId === row.uid }

                    onEditClick={ () => toggleTransactionEdit(row.uid, true) }
                    onReadonlyCategoryClick={ () => onReadonlyCategoryClick(row) }
                    onClose={ () => toggleTransactionEdit(row.uid, false) }
                  />
                )
              }
            </Table2.RowTemplate>
          </Table2.Rows>
        </Table2>
      </div>
      <Pagination
        isMobile={ isMobile }
        pageSize={ pageSize }
        paginator={ paginator }
        onPageClick={ onPageClick }
        onPageSizeClick={ onPageSizeClick }
      />
    </>
  )
}

export default TransactionsModule
